import React from "react";
import ColumnsSettingsList from "../../../components/columnsSettings";
import { RestrictedSettingsDataSelect } from "../../../components/forms/AutoCompleteFormItem";
import SalarySymbol from "../../../components/tables/SalarySymbol";
import { FormattedMessage } from "react-intl";
import { TableComponentProps, SalaryRecord } from "../types";

/**
 * ColumnsSettings Component
 *
 * Configures the columns settings specifically for salary data tables in the application.
 * This component leverages the ColumnsSettingsList to create customizable column configurations
 * that include salary symbols.
 *
 * Features:
 * - Provides a form component for selecting salary symbols
 * - Defines how salary symbol columns should be rendered in tables
 * - Integrates with the application's column settings system
 * - Supports internationalization through FormattedMessage
 *
 * The component consists of two main parts:
 * 1. A form component (Comp) that allows users to select salary symbols
 * 2. A table column configuration (tableComp) that defines how to render these symbols
 *
 * Note: This component doesn't accept any props as it's designed to be self-contained.
 *
 * @example
 * ```tsx
 * // To use this component in a settings page:
 * import ColumnsSettings from '../features/salary/settings';
 *
 * const SettingsPage = () => (
 *   <div>
 *     <h1>Salary Settings</h1>
 *     <ColumnsSettings />
 *   </div>
 * );
 * ```
 *
 * @returns {JSX.Element} A configured ColumnsSettingsList component for salary settings
 */
const ColumnsSettings: React.FC = () => {
  // Form component for the settings panel
  const Comp: React.FC = () => (
    <RestrictedSettingsDataSelect
      name="symbol_id"
      category="salary"
      settingsdataName={"system_tables/salary/salary_symbols"}
      required={true}
      paramName={"symbol"}
    />
  );

  // Table column configuration
  const tableComp: TableComponentProps = {
    title: (
      <FormattedMessage id={"app.forms.contract.shifts.salary_symbol_id"} />
    ),
    dataIndex: "symbol_id",
    render: (text: string | number, record: SalaryRecord, index: number) => (
      <SalarySymbol text={text} />
    ),
  };

  return (
    <ColumnsSettingsList
      comp={<Comp />}
      tableComp={tableComp}
      compName={"symbol_id"}
      name={"column_settings/salary"}
      type="salary"
    />
  );
};

export default ColumnsSettings;
